import { FormGroup, ValidatorFn } from '@angular/forms';
import { Column } from '../table/column';
import { Formatter } from '../table/formatter';

export type FormField = Group | FieldAndInput;
export type FieldAndInput = {
    input: FormInput;
    field: Field;
};
export type Field = {
    key: string;
    value: (value?: any) => {};
    notReturned?: boolean;
    populate?: (value: any) => any;
};

export class FormFunctions {
    static enumToSelectionFn(
        inputEnum: any,
        selector?: {
            name?: (input: string) => string;
            value?: (input: string) => unknown;
        },
    ): SelectionFn {
        return (
            data: { [key: string]: any },
            additionalFilter?: SelectionFilterFn,
        ) => {
            return Object.entries<string>(inputEnum)
                .filter((x) =>
                    additionalFilter ? additionalFilter(x, data) : true,
                )
                .map(
                    ([name, value]) =>
                        ({
                            name: selector?.name ? selector?.name(name) : name,
                            value: selector?.value
                                ? selector?.value(value)
                                : value,
                        }) satisfies SelectionOption,
                );
        };
    }
    static listToSelectionFn = <T>(
        input: T[],
        selectors: { name: (item: T) => string; value: (item: T) => unknown },
    ): SelectionFn => {
        return (
            data: { [key: string]: any },
            additionalFilter?: SelectionFilterFn,
        ) => {
            return input
                .filter((x) =>
                    additionalFilter ? additionalFilter(x, data) : true,
                )
                .map(
                    (item: T) =>
                        ({
                            name: selectors.name(item),
                            value: selectors.value(item),
                        }) satisfies SelectionOption,
                );
        };
    };

    static enumCategoryToSelectionFn = (
        categoryEnum: any,
        parent?: string,
    ): SelectionFn => {
        return (formValues: FormValues, additionalFilter) => {
            const formattedParent = parent
                ? (Formatter.toPascalCase(formValues[parent]) ?? '')
                : '';
            const parentFilter: SelectionFilterFn = (item: SelectionOption) =>
                item.name
                    ?.toLowerCase()
                    ?.includes(formattedParent.toLowerCase());

            const nameSelector = (name: string) =>
                name.replace(formattedParent, '');

            return this.enumToSelectionFn(categoryEnum)(
                formValues,
                additionalFilter,
            )
                .filter(parentFilter)
                .map((item) => ({ ...item, name: nameSelector(item.name) }));
        };
    };
}

export class FormBlueprint {
    maxHeight?: string;
    noCancelConfirm?: boolean;
    fromFields: FormField[];
    fields: Map<string, FieldAndInput> = new Map();

    getFormField(key: string): FieldAndInput {
        const field = this.fields.get(key);
        if (!field) throw new Error(`Field ${key} does not exist`);
        return field;
    }

    clearValue: (key: string) => void = () => {
        throw Error('clear function not defined');
    };
    setValue: (key: string, value: any) => void = () => {
        throw Error('set function not defined');
    };
    context: any;

    constructor(blueprint: FormField[], extraParameters?: any) {
        this.fromFields = blueprint;
        blueprint.forEach((x) => {
            const cast = x as FieldAndInput;
            if (cast.field) this.fields.set(cast.field.key, cast);
            else
                this.fields = new Map([...this.fields, ...(x as Group).fields]);
        });

        this.noCancelConfirm = extraParameters?.noCancelConfirm;
        this.maxHeight = extraParameters?.maxHeight;
    }
    get values(): FormValues {
        let values: any = {};
        this.fields.forEach(
            (x) => (values = { ...values, ...x.field.value() }),
        );
        return values;
    }
}
export class FormInput {
    type: string;
    placeholder?: string;
    required?: boolean;
    customValidator?: (input: any, formValues: any) => ValidatorFn;
    hint?: string;
    readonly?: boolean;
    updateChange?: (value: string) => void;
    fieldListener?: (
        emitterKey: string,
        fieldKey: string,
        form: any,
        values: any,
    ) => void;
    overrideFn: (input: any, override: (input: any) => any) => void;

    constructor(type: string, field?: any) {
        this.type = type;
        this.required = field.required;
        this.readonly = field.readonly ? true : false;
        this.placeholder = field.placeholder;
        this.hint = field.hint;
        this.updateChange = field.updateChange;
        this.fieldListener = field.fieldListener;
        this.customValidator = field.customValidator;
        this.overrideFn = field.overrideFn
            ? field.overrideFn
            : () => {
                  throw Error('Override Not defined');
              };
    }
}

export class TextInput extends FormInput {
    constructor(inputs: any) {
        super('textInput', inputs);
    }
}
export class TextAreaInput extends FormInput {
    autosize?: { min?: number; max?: number };
    constructor(
        inputs: any,
        options?: { autosize?: { min?: number; max?: number } },
    ) {
        super('textAreaInput', inputs);
        this.autosize = options?.autosize;
    }
}
export class NumberInput extends FormInput {
    max?: number;
    min?: number;
    constructor(inputs: any, limits: { max?: number; min?: number }) {
        super('numberInput', inputs);
        this.max = limits.max;
        this.min = limits.min;
    }
}
export class Selection extends FormInput {
    selections: (form: FormValues) => SelectionOption[];
    additionalFilter?: SelectionFilterFn;

    constructor(
        inputs: any,
        selections: SelectionFn,
        additionalFilter?: SelectionFilterFn,
    ) {
        super('selection', inputs);
        this.selections = selections;
        this.additionalFilter = additionalFilter;
    }
}

export class Autocomplete extends FormInput {
    override updateChange = (value: any) => {
        this.valueFilter =
            typeof value == 'string' ? value : this.displayValueFn(value);
    };

    selections: SelectionFn;
    valueFilter: string = '';
    additionalFilter?: SelectionFilterFn;
    displayValueFn: (value: any) => string;

    filteredSelections(formValues: any): SelectionOption[] {
        return this.selections(formValues, this.additionalFilter).filter(
            (option) =>
                option.name
                    .toLowerCase()
                    .includes(this.valueFilter.toLowerCase()),
        );
    }
    constructor(
        inputs: any,
        type: AutocompleteType,
        selections: SelectionFn,
        additionalFilter?: SelectionFilterFn,
        displayValueFn?: (value: any) => string,
    ) {
        super(type, inputs);
        this.selections = selections;
        this.additionalFilter = additionalFilter;
        this.displayValueFn = displayValueFn ?? ((value: any) => value);
    }
}
export type ListItem = {
    data: FormValues;
    itemActions?: () => { name: string; action: (input: any) => any }[];
};

export class ListInput extends FormInput {
    list: (parameter: any) => ListItem[];
    addItem?: (input: any) => void;
    itemSchema: Column[];
    constructor(
        inputs: any,
        list: (parameter: any) => ListItem[],
        itemSchema: Column[],
        addItem?: (input: any) => void,
    ) {
        super('list', inputs);
        this.list = list;
        ((this.itemSchema = itemSchema), (this.addItem = addItem));
    }
}
export class CheckBox extends FormInput {
    constructor(checkbox: any) {
        super('checkbox', checkbox);
    }
}
export class SliderInput extends FormInput {
    constructor(input: any) {
        super('slider', input);
    }
}
export class DateTimeInput extends FormInput {
    constructor(input: any) {
        super('dateTime', input);
    }
}
export class Group {
    formFields?: FormField[];
    get fields(): Map<string, FieldAndInput> {
        let fields: Map<string, FieldAndInput> = new Map();
        if (!this.formFields) return fields;
        this.formFields.forEach((field) => {
            const cast = field as FieldAndInput;
            if (cast.field) fields.set(cast.field.key, cast);
            else fields = new Map([...fields, ...(field as Group).fields]);
        });
        return fields;
    }
    caption: string;
    constructor(caption: string, fields: FormField[]) {
        this.formFields = fields;
        this.caption = caption;
    }
}
export type SelectionOption = {
    value: unknown;
    name: string;
};
export type FormValues = { [key: string]: unknown }; // TODO when rebuilding form with type safety

export type SelectionFilterFn = (item: any, data: any) => boolean;

export type SelectionFn = (
    form: FormValues,
    additionalFilter?: SelectionFilterFn,
) => SelectionOption[];

export enum AutocompleteType {
    Text = 'autocompleteTextInput',
    TextArea = 'autocompleteTextAreaInput',
}
